import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { format, isValid, parse } from 'date-fns';
import { Filter, RefreshCw } from 'lucide-react';
import { PeriodPicker } from '@/components/ui/period-picker';

const DAY_FORMAT = 'yyyy-MM-dd';

/** 'yyyy-MM-dd' → meia-noite local. `new Date('yyyy-MM-dd')` leria como UTC. */
function parseDay(value: string): Date | undefined {
  if (!value) return undefined;
  const date = parse(value, DAY_FORMAT, new Date());
  return isValid(date) ? date : undefined;
}

interface CostsFiltersProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onApply: () => void;
  onSync?: () => void;
  syncing?: boolean;
}

export function CostsFilters({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onApply,
  onSync,
  syncing = false,
}: CostsFiltersProps) {
  useLanguage();
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-50">
        <Filter size={14} className="text-blue-600" />
        <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Filtros de Período")}</span>
      </div>
      <div className="flex flex-col sm:flex-row items-end gap-3">
        <div className="flex-1 w-full">
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">{translateInterfaceText("Período")}</label>
          {/* O mês corrente (padrão da página) termina no futuro, e custos podem ser lançados adiante. */}
          <PeriodPicker
            value={{ from: parseDay(startDate), to: parseDay(endDate) }}
            onChange={({ from, to }) => {
              onStartDateChange(format(from, DAY_FORMAT));
              onEndDateChange(format(to, DAY_FORMAT));
            }}
            disableFuture={false}
            className="h-[38px] min-h-0 md:min-w-[240px]"
          />
        </div>
        <button
          onClick={onApply}
          className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99] shrink-0 h-[38px]"
        >
          <Filter size={13} /> {" "}{translateInterfaceText("Aplicar")}</button>
        {onSync && (
          <button
            onClick={onSync}
            disabled={syncing}
            className="flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-600 px-4 py-2 rounded-xl text-xs font-semibold transition-all active:scale-[0.99] shrink-0 h-[38px]"
          >
            <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} /> {" "}{translateInterfaceText("Sync AWS")}</button>
        )}
      </div>
    </div>
  );
}

export default CostsFilters;
